import { DataTableFilterOption } from '../../shared/components/data-table';
import {
  FilterCategory,
  FilterOption,
} from '../../shared/components/filter-modal/filter-modal.component';
import { DatePresetId, resolveDatePreset } from '../../reports/helpers/date-presets.helper';
import { formatDateToISO, getUserTimezone } from '../../shared/utils/date.util';
import {
  DISCOUNT_STATUS_LIST_FILTER_OPTIONS,
  DISCOUNT_TYPE_LIST_FILTER_OPTIONS,
  DiscountListFilterOption,
} from '../constants/discount.constants';
import {
  CustomDiscountListParams,
  DiscountListParams,
  DiscountType,
} from '../models/discount.models';

/** UI filter state for the Discount listing (checkbox tokens, not API values). */
export interface DiscountAppliedFilters {
  status: string[];
  discountType: string[];
  /**
   * Created Date range for both tabs: `custom` when a range is set in the Filter modal, null
   * for no date filter. Legacy preset tokens (`today`…`year`) are still understood by
   * normalizeCustomDiscountDateRangeToken so saved/queried values keep working.
   */
  dateRange: string | null;
  customStartDate: Date | null;
  customEndDate: Date | null;
  /**
   * Location ids, multi-select. Empty means all locations — the toolbar's single-select
   * dropdown moved into the Filter modal so several locations can be combined.
   */
  locations: string[];
  /** Custom Discount tab — attendant user ids (`orders.user_id` / applied_by). */
  attendants: string[];
  minAmount: number | null;
  maxAmount: number | null;
}

export function createDefaultDiscountAppliedFilters(): DiscountAppliedFilters {
  return {
    status: [],
    discountType: [],
    /**
     * No date filter by default. The toolbar period dropdown was removed, so a default of
     * `today` would hide rows behind a control the user can no longer see; the Created Date
     * range in the Filter modal is opt-in instead.
     */
    dateRange: null,
    customStartDate: null,
    customEndDate: null,
    locations: [],
    attendants: [],
    minAmount: null,
    maxAmount: null,
  };
}

export function toDataTableFilterOptions<TApi>(
  options: DiscountListFilterOption<TApi>[]
): DataTableFilterOption[] {
  return options.map(({ label, value }) => ({ label, value }));
}

export function toFilterModalOptions<TApi>(
  options: DiscountListFilterOption<TApi>[],
  selectedValues: string[]
): FilterOption[] {
  const selected = new Set(selectedValues);
  return options.map(({ value, label }) => ({
    id: value,
    label,
    value,
    selected: selected.has(value),
  }));
}

function normalizeIdList(ids: string[] | undefined | null): string[] {
  if (!Array.isArray(ids) || ids.length === 0) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const value = String(raw ?? '').trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    out.push(value);
  }
  return out;
}

function buildDynamicMultiOptions(
  items: { id: string; label: string }[],
  selectedValues: string[],
  idPrefix: string
): FilterOption[] {
  const selected = new Set(normalizeIdList(selectedValues));
  const options: FilterOption[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    const value = String(item.id ?? '').trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    options.push({
      id: `${idPrefix}-${value}`,
      label: item.label || value,
      value,
      selected: selected.has(value),
    });
  }

  for (const selectedId of selected) {
    if (seen.has(selectedId)) {
      continue;
    }
    seen.add(selectedId);
    options.push({
      id: `${idPrefix}-${selectedId}`,
      label: selectedId,
      value: selectedId,
      selected: true,
    });
  }

  return options;
}

export interface DiscountFilterOptionSources {
  locations?: { id: string; label: string }[];
  attendants?: { id: string; label: string }[];
}

/** Filter modal categories for the Discount screen (stable reference; do not rebuild each CD cycle). */
export function buildDiscountFilterCategories(
  isSummaryTab: boolean,
  appliedFilters: DiscountAppliedFilters,
  sources: DiscountFilterOptionSources = {}
): FilterCategory[] {
  const categories: FilterCategory[] = [];

  categories.push({
    id: 'location',
    name: 'Location',
    type: 'multi-dropdown',
    options: buildDynamicMultiOptions(
      sources.locations ?? [],
      appliedFilters.locations,
      'location'
    ),
  });

  if (isSummaryTab) {
    categories.push({
      id: 'status',
      name: 'Status',
      type: 'checkbox',
      options: toFilterModalOptions(DISCOUNT_STATUS_LIST_FILTER_OPTIONS, appliedFilters.status),
    });
    categories.push({
      id: 'discountType',
      name: 'Type',
      type: 'checkbox',
      options: toFilterModalOptions(DISCOUNT_TYPE_LIST_FILTER_OPTIONS, appliedFilters.discountType),
    });
    categories.push({
      id: 'createdDate',
      name: 'Custom Date',
      type: 'date-range',
      options: [],
    });
    return categories;
  }

  // Custom Discount Filter popup: Location → Type → Attendant → Amount Range → Created Date.
  // Location and the date range both moved in here from the toolbar.
  categories.push({
    id: 'discountType',
    name: 'Type',
    type: 'checkbox',
    options: toFilterModalOptions(DISCOUNT_TYPE_LIST_FILTER_OPTIONS, appliedFilters.discountType),
  });

  categories.push({
    id: 'attendant',
    name: 'Attendant',
    type: 'multi-dropdown',
    options: buildDynamicMultiOptions(
      sources.attendants ?? [],
      appliedFilters.attendants,
      'attendant'
    ),
  });

  categories.push({
    id: 'amountRange',
    name: 'Amount Range',
    type: 'amount-range',
    options: [],
  });

  categories.push({
    id: 'createdDate',
    name: 'Custom Date',
    type: 'date-range',
    options: [],
  });

  return categories;
}

/**
 * Maps checkbox multi-select UI tokens to API values. Cleared selection omits the
 * filter; one or more selections are sent as repeated query parameters.
 */
function mapMultiSelectFilter<TApi>(
  selectedUiValues: string[],
  options: DiscountListFilterOption<TApi>[]
): TApi | TApi[] | undefined {
  const apiValues = resolveSelectedApiValues(selectedUiValues, options);

  if (apiValues.length === 0) {
    return undefined;
  }

  if (apiValues.length === 1) {
    return apiValues[0];
  }

  return apiValues;
}

function resolveSelectedApiValues<TApi>(
  selectedUiValues: string[],
  options: DiscountListFilterOption<TApi>[]
): TApi[] {
  const optionByValue = new Map(options.map(option => [option.value, option.apiValue]));

  return selectedUiValues
    .map(value => optionByValue.get(value))
    .filter((value): value is TApi => value !== undefined);
}

function mapDiscountTypeFilter(selected: string[]): DiscountType | DiscountType[] | undefined {
  return mapMultiSelectFilter(selected, DISCOUNT_TYPE_LIST_FILTER_OPTIONS);
}

function mapStatusFilter(selected: string[]): boolean | boolean[] | undefined {
  return mapMultiSelectFilter(selected, DISCOUNT_STATUS_LIST_FILTER_OPTIONS);
}

/** Map admin-dashboard date-range tokens to report preset ids. */
function toDatePresetId(filterBy: string): DatePresetId | null {
  switch (filterBy) {
    case 'today':
      return 'today';
    case 'yesterday':
      return 'yesterday';
    case 'week':
      return 'this-week';
    case 'month':
      return 'this-month';
    case 'year':
      return 'this-year';
    case 'custom':
      return 'custom';
    default:
      return null;
  }
}

/**
 * Resolve Custom Discount `start_date` / `end_date` from the toolbar period.
 * Filters on `orders.created_at` (Created Date column) via the existing API.
 * Accepts both filter-modal tokens (`custom`) and payments toolbar (`Custom`).
 */
export function resolveCustomDiscountDateRangeParams(
  filters: DiscountAppliedFilters
): Pick<CustomDiscountListParams, 'start_date' | 'end_date'> {
  const dateRangeToken = normalizeCustomDiscountDateRangeToken(filters.dateRange);
  if (!dateRangeToken) {
    return { start_date: undefined, end_date: undefined };
  }

  if (dateRangeToken === 'custom') {
    if (!filters.customStartDate || !filters.customEndDate) {
      return { start_date: undefined, end_date: undefined };
    }
    return {
      start_date: formatDateToISO(filters.customStartDate),
      end_date: formatDateToISO(filters.customEndDate),
    };
  }

  const presetId = toDatePresetId(dateRangeToken);
  if (!presetId || presetId === 'custom') {
    return { start_date: undefined, end_date: undefined };
  }

  const range = resolveDatePreset(presetId, new Date(), getUserTimezone());
  if (!range) {
    return { start_date: undefined, end_date: undefined };
  }

  return {
    start_date: formatDateToISO(range.start),
    end_date: formatDateToISO(range.end),
  };
}

/** Map payments toolbar period values onto the custom-discount date-range tokens. */
export function normalizeCustomDiscountDateRangeToken(
  period: string | null | undefined
): string | null {
  if (!period) {
    return null;
  }
  const trimmed = period.trim();
  if (trimmed === 'Custom' || trimmed.toLowerCase() === 'custom') {
    return 'custom';
  }
  return trimmed;
}

function optionalIdList(ids: string[]): string | string[] | undefined {
  const normalized = normalizeIdList(ids);
  if (normalized.length === 0) {
    return undefined;
  }
  return normalized.length === 1 ? normalized[0] : normalized;
}

function optionalAmount(value: number | null | undefined): number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  return Number.isFinite(value) ? value : undefined;
}

/** Discounts Summary tab — maps UI filters to `GET package/discounts` query params. */
export function buildDiscountSummaryListApiFilters(
  filters: DiscountAppliedFilters
): Pick<
  DiscountListParams,
  'discount_type' | 'is_active' | 'created_start_date' | 'created_end_date' | 'location_id'
> {
  const dateRange = resolveCustomDiscountDateRangeParams(filters);
  const locationId = optionalIdList(filters.locations);
  return {
    discount_type: mapDiscountTypeFilter(filters.discountType),
    is_active: mapStatusFilter(filters.status),
    ...(locationId !== undefined ? { location_id: locationId } : {}),
    ...(dateRange.start_date ? { created_start_date: dateRange.start_date } : {}),
    ...(dateRange.end_date ? { created_end_date: dateRange.end_date } : {}),
  };
}

/** Custom Discount tab — maps UI filters to `GET order/custom-discounts` query params. */
export function buildCustomDiscountListApiFilters(
  filters: DiscountAppliedFilters
): Pick<
  CustomDiscountListParams,
  | 'discount_type'
  | 'start_date'
  | 'end_date'
  | 'applied_by'
  | 'min_amount'
  | 'max_amount'
  | 'location_id'
> {
  const dateRange = resolveCustomDiscountDateRangeParams(filters);
  const discountType = mapDiscountTypeFilter(filters.discountType);
  const locationId = optionalIdList(filters.locations);
  const appliedBy = optionalIdList(filters.attendants);
  const minAmount = optionalAmount(filters.minAmount);
  const maxAmount = optionalAmount(filters.maxAmount);

  return {
    ...(discountType !== undefined ? { discount_type: discountType } : {}),
    ...(locationId !== undefined ? { location_id: locationId } : {}),
    ...(dateRange.start_date ? { start_date: dateRange.start_date } : {}),
    ...(dateRange.end_date ? { end_date: dateRange.end_date } : {}),
    ...(appliedBy !== undefined ? { applied_by: appliedBy } : {}),
    ...(minAmount !== undefined ? { min_amount: minAmount } : {}),
    ...(maxAmount !== undefined ? { max_amount: maxAmount } : {}),
  };
}

/**
 * Active filter badge count for the Discount Filter button.
 * Location and period live outside the modal and are not counted.
 */
export function countActiveDiscountFilters(
  isSummaryTab: boolean,
  filters: DiscountAppliedFilters
): number {
  let count = filters.discountType.length;
  if (filters.locations.length > 0) {
    count += 1;
  }
  // Counted on both tabs: with the toolbar period gone, the badge is the only hint that a date
  // range is narrowing the list.
  if (filters.customStartDate !== null || filters.customEndDate !== null) {
    count += 1;
  }
  if (isSummaryTab) {
    return count + filters.status.length;
  }

  if (filters.attendants.length > 0) {
    count += 1;
  }
  if (filters.minAmount !== null || filters.maxAmount !== null) {
    count += 1;
  }
  return count;
}

/**
 * Read Summary Filter-modal selections.
 * Preserves toolbar period/date state — Location and Date are not in the modal.
 */
export function resolveSummaryFiltersFromCategories(
  categories: FilterCategory[],
  previous: DiscountAppliedFilters
): DiscountAppliedFilters {
  const selectedValues = (categoryId: string): string[] =>
    categories
      .find(category => category.id === categoryId)
      ?.options.filter(option => option.selected)
      .map(option => option.value) || [];

  return {
    ...previous,
    status: selectedValues('status'),
    discountType: selectedValues('discountType'),
    locations: normalizeIdList(selectedValues('location')),
    attendants: [],
    minAmount: null,
    maxAmount: null,
  };
}

/**
 * Read Custom Discount Filter-modal selections.
 * Preserves toolbar period/date state — Location and Date are not in the modal.
 */
export function resolveCustomDiscountFiltersFromCategories(
  categories: FilterCategory[],
  previous: DiscountAppliedFilters,
  minAmount: number | null,
  maxAmount: number | null
): DiscountAppliedFilters {
  const selectedValues = (categoryId: string): string[] =>
    categories
      .find(category => category.id === categoryId)
      ?.options.filter(option => option.selected)
      .map(option => option.value) || [];

  return {
    status: [],
    discountType: selectedValues('discountType'),
    dateRange: previous.dateRange,
    customStartDate: previous.customStartDate,
    customEndDate: previous.customEndDate,
    locations: normalizeIdList(selectedValues('location')),
    attendants: normalizeIdList(selectedValues('attendant')),
    minAmount,
    maxAmount,
  };
}
